import { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  chakra,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  VStack,
} from '@chakra-ui/react'
import { ComponentProps, TrackEvent } from '@/baseComponents'
import { Button } from '@/components/atoms/button'
import { FilterGroup } from '@/utils/filterOptionsContext'
import { Filters, useFilteredCardStore } from '@/stores/filteredCardStore'
import { QueryParametersStoreContext } from '@/stores/queryParametersStore'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { ButtonVariants } from '@/types/enums'
import { usePathname } from 'next/navigation'
import { useStore } from 'zustand'

export interface FilterProps extends ComponentProps {
  filters: FilterGroup[]
  clearFiltersText?: string
  setFilters?: (e: HTMLElement) => void
  resetPagination: () => void
}

export default chakra(Filter)
export function Filter({
  filters,
  clearFiltersText,
  resetPagination,
  theme = 'brand',
  ...props
}: FilterProps): JSX.Element {
  const filterWrapperRef = useRef<HTMLDivElement>(null)
  const checkboxGroupRef = useRef<HTMLDivElement>(null)
  const { updateSelectedFilters, selectedFilters } = useFilteredCardStore(
    (state) => state,
  )
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([])
  const pathName = usePathname()
  const queryParamContext = useContext(QueryParametersStoreContext)
  if (!queryParamContext) {
      throw new Error('Search params store not found in tree')
  }
  const queryParamStore = useStore(queryParamContext);
  const queryParams = queryParamStore.query;

  function resetFilters() {
    queryParamStore.resetQueryParams()
    window.history.replaceState({}, '', pathName)
    updateSelectedFilters({})
    resetPagination?.()
  }

  useEffect(() => {
      if (typeof window !== undefined) {
          let url = pathName;
          if (queryParamStore?.queryString) {
              url += `?${queryParamStore?.queryString?.toString()}`;
          }
          window.history.replaceState(
              {},
              '',
              url,
          )
      }
      // if filters are changed, reset pagination to show page 1
      resetPagination?.()
  }, [pathName, queryParamStore.queryString])

  useEffect(() => {      
      const params = queryParams || new URLSearchParams(window.location.search)?.get('query') || ''
      if (!params) {
          return
      }
      const parsedQuery = params.split('|').reduce((acc, item) => {
          const [key, value] = item.split(':')
          if (value) {
            acc[key] = value.split(',')
          }
          return acc
      }, {} as { [key: string]: string[] })
      
      updateSelectedFilters(parsedQuery)

      return () => {
          if (!!queryParamStore.queryString) {
              return
          }

          resetFilters();
      }
      // disable eslint rule because it needs to run on page render
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleSelectedFilters(updatedFilters: Filters) {
    if (!updatedFilters) {
      return
    }

    let flattenedFilterGroup = ''
    const filterEntries = Object.entries(updatedFilters)
    filterEntries.forEach(([key, values], index) => {
      if (values.length > 0) {
        flattenedFilterGroup += `${key}:${values.join(',')}`
      }

      if (index < filterEntries.length - 1) {
        flattenedFilterGroup += '|'
      }
    })
  
    TrackEvent('filter_interaction', {
      interactionType: 'filtering',
      filters: flattenedFilterGroup,
    })

    // Update the query parameters in the store
    queryParamStore.updateQueryParams({
      query: flattenedFilterGroup,
    })
    updateSelectedFilters(updatedFilters)
  }

  const FilterGroup = ({ group }: { group: FilterGroup }) => {
    if (group.filters.length === 0) {
      return null
    }

    const hasSelectedFilters = selectedFilters?.[group.groupName]?.length > 0

    return (
      <VStack width='100%' gap={1} position='relative'>
        {/* Dropdown main component */}
        <Flex
          justifyContent='space-between'
          width='100%'
          onClick={() => {
            let isOpen: boolean
            const identifier = group.groupName
            if (openDropdowns.includes(identifier)) {
              setOpenDropdowns(
                openDropdowns.filter((item) => item !== identifier),
              )
              isOpen = false
            } else {
              setOpenDropdowns([...openDropdowns, identifier])
              isOpen = true
            }
            TrackEvent('filter_interaction', {
              interactionType: isOpen
                ? 'open filter dropdown'
                : 'close filter dropdown',
              filterGroup: group.groupName,
            })
          }}
          position='relative'
          borderWidth={0.5}
          borderStyle='solid'
          borderColor='brand.grey'
          borderRadius='lg'
          backgroundColor='brand.grey'
          px={4}
          py={hasSelectedFilters ? '9.5px' : 4}
          _hover={{
            cursor: 'pointer',
            borderColor: 'brand.aubergine',
            color: hasSelectedFilters ? 'brand.dark' : 'brand.aubergine',
          }}
        >
          <VStack alignItems='start' gap={0}>
            {/* Group Label */}
            <chakra.span
              pt={1}
              style={
                hasSelectedFilters
                  ? {
                      color: 'brand.dark',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      lineHeight: 1,
                      opacity: 0.6,
                    }
                  : {
                      opacity: 1,
                      color: 'brand.aubergine',
                      fontSize: '1rem',
                    }
              }
            >
              {group.groupDisplayName}
            </chakra.span>

            {/* Selected Filters */}
            {hasSelectedFilters && (
              // truncate end of string if it is too long
              <chakra.span
                noOfLines={1}
                overflow='hidden'
                textOverflow='ellipsis'
                maxWidth='100%'
                color='brand.dark'
                pb={0}
                fontSize='md'
              >
                {/* get display name from active filters and strip out null results */}
                {group.filters
                  .map((filter) => {
                    if (
                      selectedFilters?.[group.groupName]?.includes(filter.value)
                    ) {
                      return filter.displayName ?? filter.value
                    }
                  })
                  .filter((f) => f)
                  .join(', ')}
              </chakra.span>
            )}
          </VStack>
          <HStack>
            {/* Dropdown icon + selected filters counter */}
            <ChevronDownIcon
              color='brand.aubergine'
              boxSize={6}
              transform={`${openDropdowns.includes(group.groupName) ? 'rotate(180deg)' : 'rotate(0deg)'}`}
            />
            {hasSelectedFilters && (
              <Flex
                backgroundColor='brand.aubergine'
                display='flex'
                alignItems='center'
                justifyContent='center'
                px={2}
                pt={2}
                pb={1}
                minWidth='24px'
                height='24px'
                color='brand.white'
                borderRadius={6}
                fontSize='md'
                lineHeight={0}
              >
                {selectedFilters?.[group.groupName].length}
              </Flex>
            )}
          </HStack>
        </Flex>
        {/* Dropdown with filter options */}
        {openDropdowns.includes(group.groupName) && (
          <Box
            width='100%'
            backgroundColor='brand.grey'
            borderRadius='lg'
            marginTop={1}
            boxShadow='brand.dark'             
          >
            <CheckboxGroup
              size='md'
              colorScheme='brand.aubergine'
              value={selectedFilters?.[group.groupName]}
              onChange={(value: string[]) => {
                const updatedFilters = {
                  ...selectedFilters,
                  [group.groupName]: value,
                }

                handleSelectedFilters(updatedFilters)
              }}
            >
              <VStack gap={1}>
                {group.filters.map((filter, index) => (
                  <Checkbox
                    data-filter-group={group.groupName}
                    value={filter.value}
                    key={index}
                  >
                    {filter.displayName || filter.value}
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          </Box>
        )}
      </VStack>
    )
  }

  return (
    <Box ref={filterWrapperRef}>
      <Flex
        direction='column'
        ref={checkboxGroupRef}
        gap={4}
        alignItems='start'
        {...props}
      >
        {filters?.map((group, groupIndex) => (
          <FilterGroup key={groupIndex} group={group} />
        ))}
        {clearFiltersText && (
          <Button
            onClick={() => {
              resetFilters()
            }}
            text={clearFiltersText}
            buttonVariant={ButtonVariants.Secondary}
          />
        )}
      </Flex>
    </Box>
  )
}
